import React, { useState, useEffect, useCallback } from "react";
import Styles from "./AcceptTermsCondition.module.css";
import { Modal } from "@mui/material";
import { Close } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { formatDate, formatSlotTime } from "../../../../helpers/dateFormat";
import ConfirmationModal from "../../../../commons/ConfirmationModal/ConfirmationModal";
import { getDamData } from "../../../../Services/BuySell/Listing";
import Marquee from "react-fast-marquee";

const AcceptTermsCondition = ({
  open,
  handleClose,
  data,
  handleTermsSubmit,
  wholeData,
  handleWithdraw,
  withdrawLoading,
  termsSubmitLoading,
  confirmOpen,
  setConfirmOpen,
  rejectOpen,
  setRejectOpen,
}) => {
  const [uniqueSelfReq, setUniqueSelfReq] = useState([]);
  const [selectedSelfReq, setSelectedSelfReq] = useState(null);
  const [dataWithIndex, setDataWithIndex] = useState([]);

  const user = useSelector(
    (state) => state.persistedReducer.user?.activeRole
  )?.toLowerCase();

  useEffect(() => {
    // Step 1: Build mapping from selfSlot.id to index in wholeData.matches
    let idToIndexMap = {};
    if (wholeData && Array.isArray(wholeData.matches)) {
      wholeData.matches.forEach((item, idx) => {
        if (item.selfSlot?.id !== undefined) {
          idToIndexMap[item.selfSlot.id] = idx; // 0-based index
        }
      });
    }

    // Step 2: Add index to each selfSlot in data.matches
    const addIndexToMatches = (dataObj) => {
      if (!dataObj || !Array.isArray(dataObj.matches))
        return { ...dataObj, matches: [] };
      return {
        ...dataObj,
        matches: dataObj.matches.map((item) => ({
          ...item,
          selfSlot: {
            ...item.selfSlot,
            index: idToIndexMap[item.selfSlot.id] ?? 0,
          },
        })),
      };
    };

    if (data) setDataWithIndex(addIndexToMatches(data));
    else setDataWithIndex({ matches: [] });
  }, [wholeData, data]);

  function allStatusesPending(data) {
    if (!data?.matches) return false;
    return data.matches.every((match) =>
      (match.generators || []).every((generator) =>
        (generator.matchSlots || []).every(
          (slot) => slot.finalizedConnectionValues?.status === "PENDING"
        )
      )
    );
  }

  // Navigation handlers
  const goToPrevReq = () => {
    const currentIdx = uniqueSelfReq.findIndex(
      (req) =>
        req.selfRequisitionIndex === selectedSelfReq?.selfRequisitionIndex
    );
    if (currentIdx > 0) setSelectedSelfReq(uniqueSelfReq[currentIdx - 1]);
  };

  const goToNextReq = () => {
    const currentIdx = uniqueSelfReq.findIndex(
      (req) =>
        req.selfRequisitionIndex === selectedSelfReq?.selfRequisitionIndex
    );
    if (currentIdx < uniqueSelfReq.length - 1)
      setSelectedSelfReq(uniqueSelfReq[currentIdx + 1]);
  };

  // Determine if user is consumer or generator and get appropriate data
  const isConsumer = user === "consumer";
  const isGenerator = user === "generator";

  // Get counterparties (generators for consumer, consumers for generator)
  const getCounterparties = (match) => {
    return isConsumer ? match.generators : match.consumers;
  };

  // Get capacity data based on user role
  const getCapacityData = () => {
    if (isConsumer) {
      return dataWithIndex?.capacityConsumer || {};
    } else if (isGenerator) {
      return dataWithIndex?.capacityGenerator || {};
    }
    return {};
  };

  const userDataKey =
    user === "consumer" ? "capacityConsumer" : "capacityGenerator";

  // Extract unique requisitions on data change
  useEffect(() => {
    if (!dataWithIndex?.matches) return;
    const allSelfSlots = dataWithIndex.matches.map((match, i) => ({
      id: i,
      reqId: match.selfSlot.index + 1,
      selfRequisitionIndex: match.selfSlot.id,
    }));
    const unique = allSelfSlots.filter(
      (item, idx, arr) =>
        arr.findIndex(
          (i) => i.selfRequisitionIndex === item.selfRequisitionIndex
        ) === idx
    );
    setUniqueSelfReq(unique);
    setSelectedSelfReq(unique[0]);
  }, [dataWithIndex]);

  const getStyledValue = (original, finalized, formatter = (v) => v) => {
    const formattedOriginal = formatter(original);
    const formattedFinalized = formatter(finalized);
    return (
      <div
        style={{
          fontWeight: 500,
          color:
            formattedOriginal !== formattedFinalized ? "#f1935c" : "inherit",
        }}
      >
        {formattedFinalized}
      </div>
    );
  };

  const [damData, setDamData] = useState(null);
  console.debug("damData", damData);

  const damFetch = useCallback(async () => {
    try {
      const data = await getDamData();
      setDamData(data);
    } catch (error) {
      console.error("Error fetching DAM data:", error);
    }
  }, []);

  useEffect(() => {
    damFetch();
  }, [damFetch]);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="child-modal-title"
      aria-describedby="child-modal-description"
    >
      <div className={`popupModal ${Styles.modalMainCont}`}>
        <div className="modalHeading">
          {dataWithIndex?.[userDataKey]?.initiatorType !== user ? (
            <div>Approve T&amp;C</div>
          ) : (
            <div>View T&amp;C</div>
          )}

          <Close
            onClick={handleClose}
            style={{ cursor: "pointer" }}
            alterbutton="false"
          />
        </div>
        <div className={Styles.scheduleStrip}>
          <Marquee
            pauseOnHover={true}
            gradient={false}
            speed={50}
            autoFill={true}
          >
            {" "}
            <div style={{ margin: "0px 10px" }} className={Styles.stripBox}>
              <div>Current DAM:-</div>
              <div>{Number(damData?.cur_day).toFixed(2) + " ₹/kwh" || "-"}</div>
            </div>
            <div style={{ margin: "0px 10px" }} className={Styles.stripBox}>
              <div>Previous DAM:-</div>
              <div>
                {Number(damData?.prev_day).toFixed(2) + " ₹/kwh" || "-"}
              </div>
            </div>
          </Marquee>
        </div>
        <div className={Styles.termsMainContainer}>
          {/* Requisition Tabs UI */}
          <div className={Styles.reqTabsContainer}>
            <button
              className={Styles.arrowBtn}
              onClick={goToPrevReq}
              disabled={
                uniqueSelfReq.findIndex(
                  (req) =>
                    req.selfRequisitionIndex ===
                    selectedSelfReq?.selfRequisitionIndex
                ) === 0
              }
            >
              &laquo;
            </button>
            {uniqueSelfReq?.map((req, idx) => {
              return (
                <button
                  key={req.selfRequisitionIndex}
                  className={
                    selectedSelfReq?.selfRequisitionIndex ===
                    req.selfRequisitionIndex
                      ? Styles.reqTabActive
                      : Styles.reqTab
                  }
                  onClick={() => setSelectedSelfReq(req)}
                >
                  {`Requisition ${idx + 1}`}
                </button>
              );
            })}
            <button
              className={Styles.arrowBtn}
              onClick={goToNextReq}
              disabled={
                uniqueSelfReq.findIndex(
                  (req) =>
                    req.selfRequisitionIndex ===
                    selectedSelfReq?.selfRequisitionIndex
                ) ===
                uniqueSelfReq.length - 1
              }
            >
              &raquo;
            </button>
          </div>
          <div className={Styles.tableHeadingCustom}>
            <div>Category</div>
            {/* <div>My Listing</div> */}
            <div>Counterparty Listing</div>
            <div>Final Values</div>
          </div>
          {selectedSelfReq &&
            dataWithIndex?.matches?.[selectedSelfReq.id] &&
            getCounterparties(dataWithIndex.matches[selectedSelfReq.id]).map(
              (item) => {
                return (
                  <div
                    key={item.bidId}
                    className={Styles.tableDetailsMainContCustom}
                  >
                    <div className={Styles.sectionBox}>
                      <div className={Styles.row}>
                        <div>Name</div>
                        <div style={{ fontWeight: 600, fontSize: "12px" }}>
                          {item.companyName}
                        </div>
                        <div></div>
                      </div>

                      <div className={Styles.row}>
                        <div>Initial Margin</div>
                        {/* <div>{capacityData.initialMargin}%</div> */}
                        <div style={{ fontWeight: 500 }}>
                          {item.initialMargin}%
                        </div>
                        {getStyledValue(
                          item.initialMargin,
                          item.finalizedPaymentDetails.initialMargin,
                          (v) => `${v}%`
                        )}
                      </div>

                      <div className={Styles.row}>
                        <div>EMD</div>
                        {/* <div>{capacityData.emd}%</div> */}
                        <div style={{ fontWeight: 500 }}>
                          {item.emd} (₹/kWh)
                        </div>
                        {getStyledValue(
                          item.emd,
                          item.finalizedPaymentDetails.emd,
                          (v) => `${v} (₹/kWh)`
                        )}
                      </div>

                      <div className={Styles.row}>
                        <div>Payment Security</div>
                        {/* <div>{capacityData.paymentSecurity}</div> */}
                        <div style={{ fontWeight: 500 }}>
                          {item.paymentSecurity} Days
                        </div>
                        {getStyledValue(
                          item.paymentSecurity,
                          item.finalizedPaymentDetails.paymentSecurity,
                          (v) => `${v} Days`
                        )}
                      </div>
                    </div>
                    {console.debug("item.matchSlots", item.matchSlots)}
                    {item.matchSlots.map((matchSlot, idx) => {
                      return (
                        <div className={Styles.reqSection} key={matchSlot.id}>
                          <div className={Styles.reqHeader}>
                            Requisition {idx + 1}
                          </div>
                          <div className={Styles.row}>
                            <div>Energy Rate</div>
                            <div
                              style={{ fontWeight: 500 }}
                              className={Styles.greenText}
                            >
                              {matchSlot?.energyRateStr ||
                                matchSlot.sellingCost ||
                                matchSlot.landingCost}{" "}
                              (₹/kWh)
                            </div>
                            {console.debug("matchSlot", matchSlot)}
                            {getStyledValue(
                              matchSlot?.energyRateStr ||
                                matchSlot.sellingCost ||
                                matchSlot.landingCost,
                              matchSlot.finalizedConnectionValues
                                ?.energyRateType === "FIXED"
                                ? matchSlot.finalizedConnectionValues.amount
                                : `${
                                    matchSlot.finalizedConnectionValues
                                      ?.energyRateType === "DAM_PLUS"
                                      ? "DAM+"
                                      : "DAM-"
                                  }${
                                    matchSlot.finalizedConnectionValues.amount
                                  }`,
                              (v) => `${v} (₹/kWh)`
                            )}
                          </div>
                          <div className={Styles.row}>
                            <div>Quantity</div>
                            <div
                              style={{ fontWeight: 500 }}
                              className={Styles.greenText}
                            >
                              {matchSlot.sellEnergy || matchSlot.purchaseEnergy}{" "}
                              (MW)
                            </div>
                            {getStyledValue(
                              matchSlot.sellEnergy || matchSlot.purchaseEnergy,
                              matchSlot.finalizedConnectionValues.quantity,
                              (v) => `${v} (MW)`
                            )}
                          </div>
                          <div className={Styles.row}>
                            <div>Start Date</div>
                            <div>{formatDate(matchSlot.dateStartAt)}</div>
                            {getStyledValue(
                              matchSlot.dateStartAt,
                              matchSlot.finalizedConnectionValues.dateStartAt,
                              formatDate
                            )}
                          </div>
                          <div className={Styles.row}>
                            <div>End Date</div>
                            <div style={{ fontWeight: 500 }}>
                              {formatDate(matchSlot.dateEndAt)}
                            </div>
                            {getStyledValue(
                              matchSlot.dateEndAt,
                              matchSlot.finalizedConnectionValues.dateEndAt,
                              formatDate
                            )}
                          </div>
                          <div className={Styles.row}>
                            <div>Start Time</div>
                            <div style={{ fontWeight: 500 }}>
                              {formatSlotTime(matchSlot.slotStartAt)}
                            </div>
                            {getStyledValue(
                              matchSlot.slotStartAt,
                              matchSlot.finalizedConnectionValues.slotStartAt,
                              formatSlotTime
                            )}
                          </div>
                          <div className={Styles.row}>
                            <div>End Time</div>
                            <div style={{ fontWeight: 500 }}>
                              {formatSlotTime(matchSlot.slotEndAt)}
                            </div>
                            {getStyledValue(
                              matchSlot.slotEndAt,
                              matchSlot.finalizedConnectionValues.slotEndAt,
                              formatSlotTime
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              }
            )}
        </div>
        {dataWithIndex?.[userDataKey]?.initiatorType !== user && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: "16px",
              padding: "15px 25px",
            }}
          >
            {/* <button
              style={{
                padding: "8px 24px",
                border: "1px solid #FFA55C",
                background: "#fff",
                color: "#FFA55C",
                borderRadius: "6px",
                fontWeight: 500,
                cursor: "pointer",
              }}
              onClick={goToPrevReq}
            >
              Previous
            </button> */}
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: "16px",
                width: "100%",
              }}
            >
              {/* <button
                style={{
                  padding: "8px 24px",
                  border: "none",
                  background: "var(--primary-color-text)",
                  color: "#fff",
                  borderRadius: "6px",
                  fontWeight: 500,
                  cursor: "pointer",
                }}
                onClick={() => {
                  goToNextReq();
                }}
              >
                Next
              </button> */}
              {/* <button
                style={{
                  padding: "8px 24px",
                  background: "#FFF8F9",
                  color: "#D50D21",
                  borderRadius: "6px",
                  fontWeight: 500,
                  cursor: "pointer",
                  border: "0.5px solid #D50D21",
                }}
                onClick={(e) => {
                  console.log("Rejected T&amp;C");
                }}
              >
                Reject
              </button> */}
              <button
                style={{
                  padding: "8px 24px",
                  border: "none",
                  background: "var(--primary-color-text)",
                  color: "#fff",
                  borderRadius: "6px",
                  fontWeight: 500,
                  cursor: "pointer",
                }}
                onClick={(e) => {
                  setConfirmOpen(true);
                }}
                disabled={termsSubmitLoading}
              >
                {termsSubmitLoading ? "Approving..." : "Approve T&C"}
              </button>
            </div>
          </div>
        )}

        {dataWithIndex?.[userDataKey]?.initiatorType === user &&
          allStatusesPending(dataWithIndex) && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: "16px",
                padding: "15px 25px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  gap: "16px",
                  width: "100%",
                }}
              >
                <button
                  disabled={withdrawLoading}
                  style={{
                    padding: "8px 24px",
                    background: "#FFF8F9",
                    color: "#D50D21",
                    borderRadius: "6px",
                    fontWeight: 500,
                    cursor: "pointer",
                    border: "0.5px solid #D50D21",
                  }}
                  onClick={(e) => {
                    setRejectOpen(true);
                  }}
                >
                  {withdrawLoading ? "Withdrawing..." : "Withdraw"}
                </button>
              </div>
            </div>
          )}

        <ConfirmationModal
          open={rejectOpen}
          loading={withdrawLoading}
          handleClose={() => setRejectOpen(false)}
          handleSubmit={(e) => {
            handleWithdraw(e);
          }}
          header={"Reject Terms Confirmation"}
          data={
            "Rejecting will cancel this transaction. Do you want to continue?"
          }
        />
        <ConfirmationModal
          open={confirmOpen}
          loading={termsSubmitLoading}
          handleClose={() => setConfirmOpen(false)}
          handleSubmit={(e) => {
            handleTermsSubmit(e);
          }}
          header={"Approve Terms Confirmation"}
          data={
            "Once the Terms & Conditions are accepted, no further changes will be allowed. Initial Margin will be blocked. Do you want to continue?"
          }
        />
      </div>
    </Modal>
  );
};

export default AcceptTermsCondition;
